/**
 * Teczka PME2: wspólne pomocnicze dla protokołu odbioru, sprawdzenia zgodności i ściągawki do GWD.
 *
 * Stan teczki (wszystko z formularza na stronie, nic nie idzie na serwer):
 * {
 *   imie, nazwisko,
 *   adres: { wojewodztwo, powiat, gmina, miejscowosc, ulica, nrDomu, nrLokalu, nrDzialki, kod, poczta },
 *   samodzielnie: bool, wykonawca: { nazwa, adres, nip },
 *   zakres: 'zestaw' (magazyn z nowym falownikiem hybrydowym, sekcja 2.B.1) | 'magazyn' (sam magazyn, 2.B.2),
 *   magazyn: { koszty, dataMontazu, dataOsd, pojemnosc, moc, liczbaModulow, kraj, producent, model, numery: [] },
 *   falownik: { koszty, kraj, producent, model, numer, numerStary },
 *   wyspa: 'tak' | 'nie' | '',
 *   ems: { rodzaj: 'falownik' | 'modul' | 'zewnetrzny' | '', producent, nazwa, poinformowany: bool },
 *   pv: { kwp, ppe, ppeKrajowy, dataPrzylaczenia, rozliczenie: 'net-billing' | 'net-metering' },
 *   pelnomocnik: bool, pelnomocnictwoOdreczne: bool, zmianaRozliczenia: bool,
 * }
 * Kwoty brutto, daty jako „RRRR-MM-DD” z pola daty.
 */

/** Liczba z pola formularza („12 345,50”, „12345.5”) albo null. */
export const liczba = (x) => {
  if (x === null || x === undefined || x === '') return null
  const n = Number(String(x).replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/** „12 345,50 zł” jak na fakturze (spacja niełamiąca zamieniona na zwykłą, bo PDF jej nie potrzebuje). */
export const kwota = (x) => {
  const n = liczba(x)
  if (n === null) return ''
  return `${n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/ /g, ' ')} zł`
}

/** „10,24” bez zbędnych zer. */
export const ulamek = (x) => {
  const n = liczba(x)
  return n === null ? '' : String(Math.round(n * 100) / 100).replace('.', ',')
}

/** „2026-10-20” na „20.10.2026”. Inny zapis zostaje bez zmian. */
export const data = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso || '')
}

/** Kraje UE, po polsku i po angielsku (tabliczki znamionowe), małymi literami. Premia UE do 2 000 zł. */
const KRAJE_UE = [
  ['polska', 'poland'], ['niemcy', 'germany'], ['czechy', 'czech republic', 'czechia'], ['słowacja', 'slovakia'],
  ['austria'], ['węgry', 'hungary'], ['słowenia', 'slovenia'], ['chorwacja', 'croatia'], ['włochy', 'italy'],
  ['hiszpania', 'spain'], ['portugalia', 'portugal'], ['francja', 'france'], ['belgia', 'belgium'],
  ['holandia', 'niderlandy', 'netherlands'], ['luksemburg', 'luxembourg'], ['dania', 'denmark'], ['szwecja', 'sweden'],
  ['finlandia', 'finland'], ['estonia'], ['łotwa', 'latvia'], ['litwa', 'lithuania'], ['irlandia', 'ireland'],
  ['grecja', 'greece'], ['cypr', 'cyprus'], ['malta'], ['bułgaria', 'bulgaria'], ['rumunia', 'romania'],
]

/** true / false dla znanego kraju, null gdy nie wpisano. „Made in EU” też liczymy jako UE. */
export const krajZUe = (kraj) => {
  const k = String(kraj || '').trim().toLowerCase()
  if (!k) return null
  if (/\b(ue|eu)\b|unia europejska|european union/.test(k)) return true
  return KRAJE_UE.some((nazwy) => nazwy.includes(k))
}

/** Numery seryjne bez pustych wierszy. */
export const numery = (lista) => (lista || []).map((n) => String(n || '').trim()).filter(Boolean)

/** NIP: 10 cyfr i suma kontrolna. Myślniki i spacje dopuszczalne. */
export const nipPoprawny = (nip) => {
  const c = String(nip || '').replace(/[\s-]/g, '').replace(/^PL/i, '')
  if (!/^\d{10}$/.test(c)) return false
  const wagi = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  const suma = wagi.reduce((s, w, i) => s + w * Number(c[i]), 0) % 11
  return suma !== 10 && suma === Number(c[9])
}

/**
 * Model z nazwy sklepowej z katalogu: bez producenta, słów „magazyn energii”, „inwerter hybrydowy” i pojemności
 * na końcu. „Deye magazyn energii SE-G5.1 Pro-B 5.12kWh” daje „SE-G5.1 Pro-B”. To tylko podpowiedź: wzór
 * każe wpisać model dokładnie jak w karcie produktu, więc pole zostaje do poprawienia.
 */
export const modelZNazwy = (nazwa, producent) => {
  let m = String(nazwa || '')
  if (producent) m = m.replace(new RegExp(`\\b${producent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'), ' ')
  m = m.replace(/(^|\s)(magazyn energii|moduł bateryjny|modul bateryjny|bateria|akumulator|zestaw|inwerter|falownik|hybrydowy|sieciowy|trójfazowy|jednofazowy|niskonapięciowy|wysokonapięciowy)(?=\s|$)/gi, ' ')
  m = m.replace(/(^|\s)\d+(?:[.,]\d+)?\s*kWh(?=\s|$)/gi, ' ').replace(/\s+(LV|HV)\s*$/i, '')
  return m.replace(/\s+/g, ' ').trim()
}

/** Pusty stan formularza. */
export const pustaTeczka = () => ({
  imie: '', nazwisko: '',
  adres: { wojewodztwo: '', powiat: '', gmina: '', miejscowosc: '', ulica: '', nrDomu: '', nrLokalu: '', nrDzialki: '', kod: '', poczta: '' },
  samodzielnie: false, wykonawca: { nazwa: '', adres: '', nip: '' },
  zakres: 'magazyn',
  magazyn: { koszty: '', dataMontazu: '', dataOsd: '', pojemnosc: '', moc: '', liczbaModulow: '1', kraj: '', producent: '', model: '', numery: [''] },
  falownik: { koszty: '', kraj: '', producent: '', model: '', numer: '', numerStary: '' },
  wyspa: '',
  ems: { rodzaj: '', producent: '', nazwa: '', poinformowany: false },
  pv: { kwp: '', ppe: '', ppeKrajowy: '', dataPrzylaczenia: '', rozliczenie: 'net-billing' },
  pelnomocnik: false, pelnomocnictwoOdreczne: false, zmianaRozliczenia: false,
})

/**
 * Stan formularza z zapisanych `dane`: pusta teczka uzupełniona tym, co zapisano. Części zagnieżdżone
 * łączymy osobno, żeby pole dodane w nowszej wersji formularza miało wartość domyślną w starej teczce.
 * Klucze z podkreślnikiem (`_karty`, `_zalaczniki`) nie należą do formularza.
 */
export const zDanych = (dane = {}) => {
  const pusta = pustaTeczka()
  const wynik = { ...pusta }
  for (const [k, v] of Object.entries(dane || {})) {
    if (k.startsWith('_')) continue
    wynik[k] = pusta[k] && typeof pusta[k] === 'object' && !Array.isArray(pusta[k]) && v && typeof v === 'object'
      ? { ...pusta[k], ...v } : v
  }
  if (!Array.isArray(wynik.magazyn.numery) || !wynik.magazyn.numery.length) wynik.magazyn.numery = ['']
  return wynik
}

/**
 * Teczka pod kartą klienta (CRM) bierze z karty imię, nazwisko, adres i numery PPE: dane klienta
 * wpisuje się raz, w karcie. `karta` = `{ dane }` z `GET /teczki-pme2/{id}` (`karta_klienta`) albo null.
 */
export const zKartyKlienta = (t, karta) => {
  if (!karta) return t
  const d = karta.dane || {}
  return {
    ...t,
    imie: d.imie || '',
    nazwisko: d.nazwisko || '',
    adres: { ...t.adres, ...(d.adres || {}) },
    pv: { ...t.pv, ppe: d.ppe || '', ppeKrajowy: d.ppeKrajowy || '' },
  }
}

/** Nazwa klienta na liście, gdy firma nie wpisała własnej: „Jan Kowalski, Ozorków”. */
export const nazwaKlienta = (t) => {
  const osoba = [t?.imie, t?.nazwisko].map((x) => String(x || '').trim()).filter(Boolean).join(' ')
  const miejsce = String(t?.adres?.miejscowosc || '').trim()
  return [osoba, miejsce].filter(Boolean).join(', ')
}
